'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'approval-process',
    title: 'Approval Process',
    icon: '✅',
    items: [
      {
        id: 'ap-1',
        question: 'How does the approval process work?',
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>After you submit your application, our team reviews your information — including your home details, preferred schedule, and service needs.</p>
            <p>We respond within 1–2 business days. Once approved, you receive a welcome email with next steps to set up your subscription and first cleaning appointment.</p>
          </div>
        ),
      },
      {
        id: 'ap-2',
        question: 'Why does Ordify Clean require an application?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            The application process ensures each customer is matched with the right cleaning professional and service plan. This allows us to maintain high service quality, allocate resources appropriately, and confirm we service your area before you commit to a subscription.
          </p>
        ),
      },
      {
        id: 'ap-3',
        question: 'What happens after I submit my application?',
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>Here is what to expect after submitting:</p>
            <ol className="list-decimal list-inside space-y-2 pl-2">
              <li>Submit your application.</li>
              <li>Our team reviews and approves (1–2 business days).</li>
              <li>You receive a payment link to activate your subscription.</li>
              <li>Service begins on your confirmed schedule.</li>
            </ol>
          </div>
        ),
      },
      {
        id: 'ap-4',
        question: 'Can my application be declined?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            In some cases, we are unable to accept an application if we don&apos;t currently service your area, our schedule is fully booked, or your home&apos;s requirements fall outside our current service scope. If this happens, we will notify you promptly and let you know if and when we expect to accommodate you.
          </p>
        ),
      },
      {
        id: 'ap-5',
        question: 'How long does approval take?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Most applications are reviewed within 1–2 business days. During peak periods, review takes up to 3 business days. You will receive an email notification as soon as a decision is made.
          </p>
        ),
      },
    ],
  },
  {
    id: 'subscription-details',
    title: 'Subscription Details',
    icon: '📋',
    items: [
      {
        id: 'sd-1',
        question: 'What subscription plans are available?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            We offer monthly subscription plans based on your home size and cleaning frequency. Plans are structured as weekly or bi-weekly recurring home maintenance service. Visit our{' '}
            <Link href="/monthly-cleaning-plans" className="text-primary hover:underline">
              Service Plans page
            </Link>{' '}
            to see all available options and pricing.
          </p>
        ),
      },
      {
        id: 'sd-2',
        question: 'How does billing work?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Subscriptions are billed monthly on the date you first subscribed. We accept major credit and debit cards, processed securely through Stripe. You receive an email receipt after each billing cycle. If a payment fails, we retry the charge and notify you to update your payment method.
          </p>
        ),
      },
      {
        id: 'sd-3',
        question: 'Can I change my subscription plan?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Yes. You can upgrade or downgrade your plan at any time by contacting us at{' '}
            <a href="mailto:Info@Ordifyclean.com" className="text-primary hover:underline">
              Info@Ordifyclean.com
            </a>
            . Plan changes take effect at the start of your next billing cycle. If you upgrade mid-cycle, we prorate the difference.
          </p>
        ),
      },
      {
        id: 'sd-4',
        question: 'Will my subscription price ever change?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Subscription pricing is adjusted from time to time. If we make a change, at least 30 days&apos; written notice is provided before the new pricing takes effect. Continued use of the service after the effective date constitutes acceptance of the new pricing.
          </p>
        ),
      },
      {
        id: 'sd-5',
        question: 'Is there a contract or minimum commitment?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            No long-term contracts. Subscriptions are month-to-month and are cancellable at any time. We ask for at least 7 days&apos; notice before your next billing date to avoid being charged for the following month.
          </p>
        ),
      },
    ],
  },
  {
    id: 'cancellation',
    title: 'Cancellation',
    icon: '🚫',
    items: [
      {
        id: 'ca-1',
        question: 'How do I cancel my subscription?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            To cancel, email us at{' '}
            <a href="mailto:Info@Ordifyclean.com" className="text-primary hover:underline">
              Info@Ordifyclean.com
            </a>{' '}
            at least 7 days before your next billing date. Include your name and account email. We will confirm your cancellation and provide the effective date.
          </p>
        ),
      },
      {
        id: 'ca-2',
        question: 'When does my cancellation take effect?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Cancellations take effect at the end of your current billing period. Service continues through the last day of the period already paid for. No further charges are made after that date.
          </p>
        ),
      },
      {
        id: 'ca-3',
        question: 'Will I receive a refund if I cancel mid-cycle?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Partial refunds are not provided for unused portions of a billing period. Your service remains active through the end of the current billing cycle. If you believe there are exceptional circumstances, contact us and we will review your situation on a case-by-case basis.
          </p>
        ),
      },
      {
        id: 'ca-4',
        question: 'Can I pause my subscription instead of cancelling?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            In certain circumstances, subscriptions are paused for up to 60 days. Contact us at{' '}
            <a href="mailto:Info@Ordifyclean.com" className="text-primary hover:underline">
              Info@Ordifyclean.com
            </a>{' '}
            to discuss your situation. Pauses are subject to availability and must be requested at least 7 days before your next billing date.
          </p>
        ),
      },
      {
        id: 'ca-5',
        question: 'Can I reactivate my subscription after cancelling?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Yes. If you&apos;d like to restart your service after cancelling, reach out and we will get you set back up. Depending on availability, a new application is required if significant time has passed since your original approval.
          </p>
        ),
      },
    ],
  },
  {
    id: 'service-adjustments',
    title: 'Service Adjustments',
    icon: '🔧',
    items: [
      {
        id: 'sa-1',
        question: 'Can I reschedule a cleaning appointment?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Rescheduling requests require at least 24 hours&apos; notice and are subject to availability. Rescheduling is not guaranteed. Same-day cancellations or late cancellations may result in forfeiture of the scheduled visit without refund.
          </p>
        ),
      },
      {
        id: 'sa-2',
        question: 'What if my cleaner cancels or is late?',
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>
              Because Ordify Clean manages the scheduling and coordination, you don&apos;t handle this yourself — we do.
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-foreground">Cancellation by the professional:</strong> A replacement is coordinated or your visit is rescheduled within 1–2 business days.</li>
              <li><strong className="text-foreground">Late arrival:</strong> If your professional is running significantly late, we notify you and confirm the updated arrival window. If the visit cannot proceed, it is rescheduled at no additional cost.</li>
              <li><strong className="text-foreground">No-shows:</strong> In the rare event of a no-show, contact us immediately. Rescheduling is prioritized and the situation is reviewed with the professional in our network.</li>
            </ul>
            <p>
              You are never left to figure it out on your own — that is the core of what we manage for you.
            </p>
          </div>
        ),
      },
      {
        id: 'sa-3',
        question: 'What if I need to skip a cleaning?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Recurring cleaning subscriptions are billed monthly and are not priced per individual visit. Missed or skipped visits do not qualify for refunds. Rescheduling requests require at least 24 hours&apos; notice and are subject to availability. Rescheduling is not guaranteed. Same-day cancellations or cancellations made with less than 24 hours&apos; notice may result in forfeiture of the scheduled visit without refund.
          </p>
        ),
      },
      {
        id: 'sa-4',
        question: 'Can I request additional services outside my plan?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Yes. Add-on services such as oven cleaning, fridge interior cleaning, interior cabinets, baseboards detail, and deep bathroom reset are available. Add-on services are not included in recurring cleaning visits and must be requested in advance and scheduled separately to ensure proper time and attention. Contact us to discuss your needs and get a quote.
          </p>
        ),
      },
      {
        id: 'sa-4b',
        question: 'Can I request extra services like oven or fridge cleaning?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Yes. Add-on services can be requested after approval and are scheduled separately to ensure proper time and quality.
          </p>
        ),
      },
      {
        id: 'sa-5',
        question: "What if I'm not satisfied with a cleaning?",
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>
              Your satisfaction is our responsibility. If you&apos;re not happy with a cleaning, here is what happens:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-foreground">Report within 24 hours:</strong> Contact us within 24 hours of your visit and describe the areas or issues that didn&apos;t meet your expectations.</li>
              <li><strong className="text-foreground">Re-clean:</strong> A complimentary re-clean of the specific areas in question is arranged at no additional charge.</li>
              <li><strong className="text-foreground">Ongoing quality:</strong> All feedback is reviewed and used to hold our network to a higher standard. Repeated quality issues with a specific professional result in reassignment.</li>
            </ul>
            <p>
              Every complaint is taken seriously because our reputation depends on consistent quality — not just one good visit.
            </p>
          </div>
        ),
      },
      {
        id: 'sa-6',
        question: 'What happens if something is damaged during a cleaning?',
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>
              Property damage is taken seriously and handled through a defined process.
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-foreground">Report within 48 hours:</strong> Damage must be reported within 48 hours of the cleaning visit. Contact us at <a href="mailto:Info@Ordifyclean.com" className="text-primary hover:underline">Info@Ordifyclean.com</a> with a description and photos if possible.</li>
              <li><strong className="text-foreground">Investigation:</strong> The report is reviewed with the professional involved and responsibility is assessed based on the circumstances.</li>
              <li><strong className="text-foreground">Resolution:</strong> If responsibility is determined, resolution includes repair, replacement, or limited compensation up to the value of the specific visit. Resolution is handled through a defined process — not open-ended liability.</li>
              <li><strong className="text-foreground">Excluded items:</strong> We are not responsible for damage to inherently fragile items, improperly secured objects, or items of exceptional value (antiques, artwork, jewelry) unless disclosed in advance.</li>
            </ul>
            <p>
              Securing or removing valuables before each visit is strongly recommended.
            </p>
          </div>
        ),
      },
      {
        id: 'sa-7',
        question: 'Can I choose my cleaner?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            We prioritize consistency whenever possible. However, as a managed service, we assign different professionals when needed to maintain scheduling reliability and service quality. All professionals in our network operate under the same structured standards — so results remain consistent regardless of who is assigned.
          </p>
        ),
      },
      {
        id: 'sa-8-specific',
        question: 'Can I request a specific cleaning professional?',
        answer: (
          <p className="text-muted-foreground leading-relaxed">
            Services are performed by vetted independent professionals operating under our standards. Cleaner continuity is prioritized when possible but not guaranteed. All professionals follow the same structured system to ensure consistent results. If you have a strong preference or a concern about a specific professional, let us know and we will do our best to accommodate you.
          </p>
        ),
      },
      {
        id: 'sa-8',
        question: 'What should I do to prepare for a cleaning visit?',
        answer: (
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p>To help the cleaning professional work as efficiently as possible, we recommend:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Tidying up clutter so surfaces are accessible.</li>
              <li>Securing or putting away valuables and fragile items.</li>
              <li>Ensuring the professional has access to your home (key, code, or someone present).</li>
              <li>Letting us know about any pets so we can plan accordingly.</li>
              <li>Noting any specific areas or items that need special attention.</li>
            </ul>
          </div>
        ),
      },
    ],
  },
];

const AccordionItem = ({ item }: { item: FAQItem }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-card hover:bg-muted/50 transition-colors duration-200"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-foreground pr-4">{item.question}</span>
        <span
          className={`flex-shrink-0 w-5 h-5 text-primary transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-background border-t border-border">
          {item.answer}
        </div>
      )}
    </div>
  );
};

export default function FAQContent() {
  const [activeCategory, setActiveCategory] = useState<string>('approval-process');

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero */}
      <section className="bg-card border-b border-border py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-4">
            <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg">
            Everything you need to know about our approval process, subscriptions, cancellations, and service adjustments — including what happens if something goes wrong.
          </p>
        </div>
      </section>

      <main className="flex-1 container mx-auto px-4 py-12 max-w-5xl">
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 justify-center">
          {faqCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              <span>{cat.icon}</span>
              {cat.title}
            </button>
          ))}
        </div>

        {/* FAQ Sections */}
        {faqCategories.map((category) => (
          <div
            key={category.id}
            className={activeCategory === category.id ? 'block' : 'hidden'}
          >
            <div className="mb-6">
              <h2 className="text-xl font-headline font-semibold text-foreground flex items-center gap-2">
                <span>{category.icon}</span>
                {category.title}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {category.items.length} questions
              </p>
            </div>
            <div className="space-y-3">
              {category.items.map((item) => (
                <AccordionItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Still have questions CTA */}
        <div className="mt-14 bg-card border border-border rounded-xl p-8 text-center">
          <h3 className="text-xl font-headline font-semibold text-foreground mb-2">
            Still have questions?
          </h3>
          <p className="text-muted-foreground mb-6">
            Can&apos;t find the answer you&apos;re looking for? Our support team is happy to help.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200"
          >
            Contact Us
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
